import { computed, ref } from 'vue';
import axios from 'axios';
import { getApiBaseUrl } from '@/config';

const GERMAN_MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

function fmtDelta(v) {
  return `${v >= 0 ? '+' : ''}${EUR.format(v)}`;
}
function fmtPct(v) {
  return `${v >= 0 ? '+' : ''}${v.toFixed(1).replace('.', ',')} %`;
}

// ---- Merch mapping (/api/projects/) ----
function mapMerchProject(p) {
  return {
    id: p.projekt_nummer,
    ober: 'print',
    kat: p.projekt_typ,
    katLabel: p.projekt_typ,
    shortLabel: p.projekt_name,
    sales: p.sales ?? 0,
    revenue: p.erloese ?? 0,
    result: (p.erloese ?? 0) - (p.projekt_kosten ?? 0),
    plan_sales: p.plan_sales ?? 0,
    plan_revenue: p.plan_erloese ?? 0,
    plan_kosten: p.projekt_plan_kosten ?? 0,
    reporting_period: p.reporting_period ?? '',
    projekt_jahr: p.projekt_jahr,
    sales_effizienz: p.sales_effizienz ?? null,
    operations_effizienz: p.operations_effizienz ?? null,
    // Kosten subcategories
    studienkosten_ff: p.studien_kosten_ff ?? 0,
    studienkosten_dienst: p.studien_kosten_dienst ?? 0,
    allgkosten: p.allg_kosten ?? 0,
    werbekosten: p.werbe_kosten ?? 0,
    teams: p.dict_teams_contributions ?? {},
    plan_studienkosten_ff: p.plan_studien_kosten_ff ?? 0,
    plan_studienkosten_dienst: p.plan_studien_kosten_dienst ?? 0,
    plan_allgkosten: p.plan_allg_kosten ?? 0,
    plan_werbekosten: p.plan_werbe_kosten ?? 0,
    plan_personalkosten: p.projekt_plan_personal_kosten ?? 0,
  };
}

// ---- Digital mapping (/api/digitalreport/) ----
function sumDigitalRows(arr, wertTyp) {
  const byName = {};
  for (const row of arr) {
    if (!byName[row.name_ergebniszeile]) byName[row.name_ergebniszeile] = {};
    if (row.wert_typ === 'Ist') byName[row.name_ergebniszeile].ist = row.wert_jahr;
    if (row.wert_typ === 'Plan') byName[row.name_ergebniszeile].plan = row.wert_jahr;
  }
  return Object.values(byName).reduce((sum, v) => sum + (parseFloat(v[wertTyp]) || 0), 0);
}

function mapDigitalProjects(data, berichtMonat) {
  const projects = [];
  for (const [brand, produkte] of Object.entries(data || {})) {
    for (const [produkt, details] of Object.entries(produkte)) {
      const erloeseIst = sumDigitalRows(details.erloese, 'ist');
      const erloesePlan = sumDigitalRows(details.erloese, 'plan');
      const kostenIst = sumDigitalRows(details.kosten, 'ist');
      const kostenPlan = sumDigitalRows(details.kosten, 'plan');
      const personalkosten = Object.values(details.personalkosten_teams || {})
        .reduce((s, v) => s + (v ?? 0), 0);
      // Kosten-Werte sind im Backend negativ, Ergebnis = Erlöse + Kosten + Personal
      const result = erloeseIst + kostenIst + personalkosten;
      const jahr = details.erloese[0]?.jahr ?? new Date().getFullYear();

      projects.push({
        id: `digital-${brand}-${produkt}`,
        ober: 'digital',
        kat: brand,
        katLabel: brand,
        shortLabel: produkt,
        sales: erloeseIst,
        revenue: erloeseIst,
        result,
        plan_sales: erloesePlan,
        plan_revenue: erloesePlan,
        plan_kosten: kostenPlan,
        reporting_period: berichtMonat ? `1-${berichtMonat}` : '',
        projekt_jahr: jahr,
        sales_effizienz: null,
        operations_effizienz: null,
      });
    }
  }
  return projects;
}

// ---- Period label helpers ----
function getMostCommonPeriod(projects) {
  const freq = {};
  for (const p of projects) {
    if (p.reporting_period) freq[p.reporting_period] = (freq[p.reporting_period] || 0) + 1;
  }
  const keys = Object.keys(freq);
  if (!keys.length) return null;
  return keys.reduce((a, b) => freq[a] >= freq[b] ? a : b);
}

function formatPeriodLabel(period, year) {
  if (!period || !year) return '';
  const parts = period.split('-');
  const start = String(parts[0]).padStart(2, '0');
  const end = String(parts[parts.length - 1]).padStart(2, '0');
  return `${start}-${end}/${year}`;
}

function getMonthLabel(period) {
  if (!period) return '';
  const parts = period.split('-');
  const month = parseInt(parts[parts.length - 1], 10);
  return GERMAN_MONTHS[month - 1] ?? String(month);
}

export function useDashboardState() {
  const loading = ref(true);
  const error = ref('');
  const allProjects = ref([]);
  const availableYears = ref([]);

  const selectedBereich = ref('print');
  const selectedMarke = ref('');
  const selectedProjekt = ref('');
  const selectedCompareIds = ref([]);
  const selectedYear = ref(new Date().getFullYear());
  const selectedTab = ref('dashboard');

  // ---- Year-filtered slices ----
  const projectsByYear = computed(() =>
    allProjects.value.filter(p => p.projekt_jahr === selectedYear.value)
  );
  const projectsByYearPrev = computed(() =>
    allProjects.value.filter(p => p.projekt_jahr === selectedYear.value - 1)
  );

  const projectsByBereich = computed(() =>
    projectsByYear.value.filter(p => p.ober === selectedBereich.value)
  );
  const projectsByBereichPrev = computed(() =>
    projectsByYearPrev.value.filter(p => p.ober === selectedBereich.value)
  );

  // ---- Filter options ----
  const brandOptions = computed(() => {
    const map = new Map();
    projectsByBereich.value.forEach(p => {
      if (!map.has(p.kat)) map.set(p.kat, p.katLabel);
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  });

  const mainScope = computed(() =>
    projectsByBereich.value.filter(p => p.kat === selectedMarke.value)
  );

  const mainProject = computed(() =>
    mainScope.value.find(p => p.id === selectedProjekt.value) || mainScope.value[0] || null
  );

  const compareProjects = computed(() => {
    if (!mainProject.value) return [];
    return projectsByBereich.value.filter(p =>
      selectedCompareIds.value.includes(p.id) && p.id !== mainProject.value.id
    );
  });

  const dashboardProjects = computed(() => {
    if (!mainProject.value) return [];
    return [mainProject.value, ...compareProjects.value];
  });

  const groupedBrandProjects = computed(() => {
    if (!mainProject.value) return [];
    return brandOptions.value
      .map(option => {
        const projects = projectsByBereich.value
          .filter(p => p.kat === option.value)
          .map(p => ({ ...p, isMainProject: p.id === mainProject.value.id }));
        return { kat: option.value, label: option.label, projects };
      })
      .filter(g => g.projects.length > 0);
  });

  // ---- Metadata ----
  const reportingMeta = computed(() => {
    // Always derive period from ALL projects in the current bereich (not just selected ones).
    // Mode (most frequent) reflects the actual IST coverage consistently across filter changes.
    const period = getMostCommonPeriod(projectsByBereich.value);
    return {
      reportingPeriodLabel: formatPeriodLabel(period, selectedYear.value),
      monthLabel: getMonthLabel(period),
      dataStatusLabel: '',
    };
  });

  const EFF = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

  function avgEffizienz(projects, key) {
    const vals = projects.map(p => p[key]).filter(v => v !== null && v !== undefined);
    if (!vals.length) return null;
    return vals.reduce((s, v) => s + v, 0) / vals.length;
  }

  // ---- KPI Summary cards ----
  const summaryCards = computed(() => {
    const cur = selectedTab.value === 'dashboard'
      ? dashboardProjects.value
      : (mainProject.value ? [mainProject.value] : []);
    const totalSales = cur.reduce((s, p) => s + p.sales, 0);
    const totalRevenue = cur.reduce((s, p) => s + p.revenue, 0);
    const totalResult = cur.reduce((s, p) => s + p.result, 0);
    const margin = totalRevenue > 0 ? (totalResult / totalRevenue) * 100 : 0;
    return [
      { key: 'sales', label: 'Sales Erlöse', value: EUR.format(totalSales), meta: 'Ist kumuliert' },
      { key: 'revenue', label: 'Umsatz abgegrenzt', value: EUR.format(totalRevenue), meta: 'Ist kumuliert' },
      { key: 'result', label: 'Cockpit Ergebnis', value: EUR.format(totalResult), meta: '' },
      { key: 'margin', label: 'Marge', value: `${margin.toFixed(1).replace('.', ',')} %`, meta: 'Ist in %' },
    ];
  });

  // ---- Effizienz KPI cards ----
  const kpiCards = computed(() => {
    const cur = selectedTab.value === 'dashboard'
      ? dashboardProjects.value
      : (mainProject.value ? [mainProject.value] : []);
    const salesEff = avgEffizienz(cur, 'sales_effizienz');
    const opsEff = avgEffizienz(cur, 'operations_effizienz');
    return [
      { key: 'sales_effizienz', label: 'Sales-Effizienz', value: salesEff !== null ? `${EFF.format(salesEff)} €` : 'n.a.', unit: salesEff !== null ? '/Std.' : null, meta: 'Umsatz ÷ Arbeitsstunden · SOLL: tbd' },
      { key: 'operations_effizienz', label: 'Operations-Effizienz', value: opsEff !== null ? `${EFF.format(opsEff)} €` : 'n.a.', unit: opsEff !== null ? '/Std.' : null, meta: 'Umsatz ÷ Arbeitsstunden · SOLL: tbd' },
    ];
  });

  // ---- Kennzahlen metrics rows ----
  const metricsRows = computed(() => {
    const cur = selectedTab.value === 'dashboard'
      ? dashboardProjects.value
      : (mainProject.value ? [mainProject.value] : []);
    if (!cur.length) return [];

    function projectKey(project) {
      return [project.ober, project.kat, project.shortLabel].join('::');
    }

    const selectedKeys = new Set(cur.map(projectKey));
    const prev = projectsByBereichPrev.value.filter((p) => selectedKeys.has(projectKey(p)));

    function sumOrNull(list, selector) {
      if (!list.length) return null;
      return list.reduce((sum, item) => sum + (selector(item) ?? 0), 0);
    }

    const ist_sales = cur.reduce((s, p) => s + p.sales, 0);
    const plan_sales = cur.reduce((s, p) => s + p.plan_sales, 0);
    const ist_rev = cur.reduce((s, p) => s + p.revenue, 0);
    const plan_rev = cur.reduce((s, p) => s + p.plan_revenue, 0);
    const ist_kosten = cur.reduce((s, p) => s + (p.revenue - p.result), 0);
    const plan_kosten = cur.reduce((s, p) => s + p.plan_kosten, 0);
    const ist_result = cur.reduce((s, p) => s + p.result, 0);
    const plan_result = plan_rev - plan_kosten;

    // Vorjahr ausschließlich aus IST-Werten des echten Vorjahres, gleicher Filter-Scope.
    const vj_sales = sumOrNull(prev, (p) => p.sales);
    const vj_rev = sumOrNull(prev, (p) => p.revenue);
    const vj_kosten = sumOrNull(prev, (p) => p.revenue - p.result);
    const vj_result = sumOrNull(prev, (p) => p.result);

    function makeRow(key, name, ist, plan, vj, opts = {}) {
      const { level = 0, parentKey = null, expandable = false, invertDelta = false } = opts;
      const dPlan = plan !== null ? ist - plan : null;
      const pctPlan = plan !== null && plan ? (dPlan / Math.abs(plan)) * 100 : null;
      const dVj = vj !== null ? ist - vj : null;
      const pctVj = vj !== null && vj ? (dVj / Math.abs(vj)) * 100 : null;

      function deltaClass(d) {
        if (d === null) return '';
        if (invertDelta) return d <= 0 ? 'positive' : 'negative';
        return d >= 0 ? 'positive' : 'negative';
      }

      return {
        key, name, level, parentKey, expandable,
        ist: EUR.format(ist),
        plan: plan !== null ? EUR.format(plan) : '–',
        vsPlan: dPlan !== null ? `${fmtDelta(dPlan)}\n${pctPlan !== null ? fmtPct(pctPlan) : ''}` : '–',
        vsPlanClass: deltaClass(dPlan),
        vj: vj !== null ? EUR.format(vj) : '–',
        vsVj: dVj !== null ? `${fmtDelta(dVj)}\n${pctVj !== null ? fmtPct(pctVj) : ''}` : '–',
        vsVjClass: deltaClass(dVj),
      };
    }

    // Kosten subcategories
    const ist_stud_ff = cur.reduce((s, p) => s + p.studienkosten_ff, 0);
    const ist_stud_dienst = cur.reduce((s, p) => s + p.studienkosten_dienst, 0);
    const ist_allgkosten = cur.reduce((s, p) => s + p.allgkosten, 0);
    const ist_studienkosten = ist_stud_ff + ist_allgkosten + ist_stud_dienst;
    const ist_werbekosten = cur.reduce((s, p) => s + p.werbekosten, 0);

    const plan_stud_ff = cur.reduce((s, p) => s + p.plan_studienkosten_ff, 0);
    const plan_stud_dienst = cur.reduce((s, p) => s + p.plan_studienkosten_dienst, 0);
    const plan_allgkosten = cur.reduce((s, p) => s + p.plan_allgkosten, 0);
    const plan_studienkosten = plan_stud_ff + plan_allgkosten + plan_stud_dienst;
    const plan_werbekosten = cur.reduce((s, p) => s + p.plan_werbekosten, 0);

    // Aggregate personal teams across selected projects
    const teamMap = {};
    for (const p of cur) {
      for (const [t, v] of Object.entries(p.teams || {})) {
        teamMap[t] = (teamMap[t] || 0) + (v ?? 0);
      }
    }
    const ist_personalkosten = Object.values(teamMap).reduce((s, v) => s + v, 0);

    // VJ subcategories
    const vj_stud_ff = prev.length ? prev.reduce((s, p) => s + p.studienkosten_ff, 0) : null;
    const vj_stud_dienst = prev.length ? prev.reduce((s, p) => s + p.studienkosten_dienst, 0) : null;
    const vj_allgkosten = prev.length ? prev.reduce((s, p) => s + p.allgkosten, 0) : null;
    const vj_studienkosten = prev.length ? vj_stud_ff + vj_allgkosten + vj_stud_dienst : null;
    const vj_werbekosten = prev.length ? prev.reduce((s, p) => s + p.werbekosten, 0) : null;
    const vjTeamMap = {};
    for (const p of prev) {
      for (const [t, v] of Object.entries(p.teams || {})) {
        vjTeamMap[t] = (vjTeamMap[t] || 0) + (v ?? 0);
      }
    }
    const vj_personalkosten = prev.length ? Object.values(vjTeamMap).reduce((s, v) => s + v, 0) : null;

    const teamRows = Object.entries(teamMap).map(([team, ist]) => {
      const vjTeam = prev.length ? (vjTeamMap[team] ?? 0) : null;
      return makeRow(`team_${team}`, team, ist, vjTeam, vjTeam, { level: 2, parentKey: 'personalkosten', invertDelta: true });
    });

    return [
      makeRow('sales', 'Sales Erlöse', ist_sales, plan_sales, vj_sales),
      makeRow('revenue', 'Umsatz (abgegrenzt)', ist_rev, plan_rev, vj_rev),
      makeRow('kosten', 'Kosten', ist_kosten, plan_kosten, vj_kosten, { expandable: true, invertDelta: true }),
      makeRow('studienkosten', 'Studienkosten', ist_studienkosten, plan_studienkosten, vj_studienkosten, { level: 1, parentKey: 'kosten', expandable: true, invertDelta: true }),
      makeRow('studienkosten_ff', 'FactField Studienkosten', ist_stud_ff, plan_stud_ff, vj_stud_ff, { level: 2, parentKey: 'studienkosten', invertDelta: true }),
      makeRow('allgkosten', 'FactField Umlage', ist_allgkosten, plan_allgkosten, vj_allgkosten, { level: 2, parentKey: 'studienkosten', invertDelta: true }),
      makeRow('studienkosten_dienst', 'Dienstleister Studienkosten', ist_stud_dienst, plan_stud_dienst, vj_stud_dienst, { level: 2, parentKey: 'studienkosten', invertDelta: true }),
      makeRow('werbekosten', 'Werbekosten', ist_werbekosten, plan_werbekosten, vj_werbekosten, { level: 1, parentKey: 'kosten', invertDelta: true }),
      makeRow('personalkosten', 'Personalkosten', ist_personalkosten, vj_personalkosten, vj_personalkosten, { level: 1, parentKey: 'kosten', expandable: true, invertDelta: true }),
      ...teamRows,
      makeRow('result', '= Cockpit Ergebnis', ist_result, plan_result, vj_result),
    ];
  });

  // ---- Breadcrumb ----
  const breadcrumbGroups = computed(() => {
    if (!mainProject.value) return [];
    const base = [{ marke: mainProject.value.katLabel, proj: mainProject.value.shortLabel }];
    if (selectedTab.value !== 'dashboard') return base;
    return base.concat(compareProjects.value.map(p => ({ marke: p.katLabel, proj: p.shortLabel })));
  });

  const bereichLabel = computed(() => selectedBereich.value === 'digital' ? 'Digital' : 'Merch');

  const hasData = computed(() => allProjects.value.length > 0);

  const vm = computed(() => ({
    filters: {
      bereiche: [
        { value: 'print', label: 'Merch' },
        { value: 'digital', label: 'Digital' },
      ],
      marken: brandOptions.value,
      projekte: mainScope.value.map(p => ({ value: p.id, label: p.shortLabel })),
      years: availableYears.value,
    },
    metadata: reportingMeta.value,
    summaryCards: summaryCards.value,
    kpiCards: kpiCards.value,
    groupedBrandProjects: groupedBrandProjects.value,
    mainProjectId: mainProject.value?.id ?? null,
    breadcrumbGroups: breadcrumbGroups.value,
    bereichLabel: bereichLabel.value,
    metricsRows: metricsRows.value,
    timeseries: [],
  }));

  // ---- Selection helpers ----
  function syncSelectionToBereich() {
    const first = projectsByBereich.value[0];
    if (first) {
      selectedMarke.value = first.kat;
      selectedProjekt.value = first.id;
    } else {
      selectedMarke.value = '';
      selectedProjekt.value = '';
    }
    selectedCompareIds.value = [];
  }

  // ---- API fetch ----
  async function initialize() {
    loading.value = true;
    error.value = '';
    try {
      const apiUrl = getApiBaseUrl();
      const [merchRes, digitalRes] = await Promise.all([
        axios.get(`${apiUrl}/projects/`, { withCredentials: true }),
        axios.get(`${apiUrl}/digitalreport/`, { withCredentials: true }).catch(() => ({ data: { data: {}, bericht_monat: null } })),
      ]);

      const merch = (merchRes.data.projects ?? []).map(mapMerchProject);
      const digital = mapDigitalProjects(digitalRes.data.data, digitalRes.data.bericht_monat);
      allProjects.value = [...merch, ...digital];

      const years = [...new Set(allProjects.value.map(p => p.projekt_jahr).filter(Boolean))]
        .sort((a, b) => b - a);
      availableYears.value = years;
      if (years.length && !years.includes(selectedYear.value)) {
        selectedYear.value = years[0];
      }

      syncSelectionToBereich();
    } catch (err) {
      error.value = 'Daten konnten nicht geladen werden.';
    } finally {
      loading.value = false;
    }
  }

  function updateSelection(partial) {
    if (Object.prototype.hasOwnProperty.call(partial, 'bereich')) {
      selectedBereich.value = partial.bereich;
      syncSelectionToBereich();
    }
    if (Object.prototype.hasOwnProperty.call(partial, 'marke')) {
      selectedMarke.value = partial.marke;
      const first = projectsByBereich.value.find(p => p.kat === partial.marke);
      if (first) selectedProjekt.value = first.id;
      selectedCompareIds.value = selectedCompareIds.value.filter(id => id !== selectedProjekt.value);
    }
    if (Object.prototype.hasOwnProperty.call(partial, 'projekt')) {
      selectedProjekt.value = partial.projekt;
      selectedCompareIds.value = selectedCompareIds.value.filter(id => id !== partial.projekt);
    }
    if (Object.prototype.hasOwnProperty.call(partial, 'year')) {
      selectedYear.value = partial.year;
      syncSelectionToBereich();
    }
  }

  function setTab(tab) { selectedTab.value = tab; }

  function toggleCompare(projectId) {
    if (projectId === mainProject.value?.id) return;
    if (selectedCompareIds.value.includes(projectId)) {
      selectedCompareIds.value = selectedCompareIds.value.filter(id => id !== projectId);
      return;
    }
    selectedCompareIds.value = [...selectedCompareIds.value, projectId];
  }

  function clearCompare() { selectedCompareIds.value = []; }

  function setCompareByBrand(payload) {
    const group = groupedBrandProjects.value.find(item => item.kat === payload.kat);
    if (!group) return;
    const ids = group.projects
      .filter(p => !p.isMainProject)
      .map(p => p.id);
    if (payload.on) {
      selectedCompareIds.value = [...new Set([...selectedCompareIds.value, ...ids])];
      return;
    }
    selectedCompareIds.value = selectedCompareIds.value.filter(id => !ids.includes(id));
  }

  return {
    loading,
    error,
    selectedBereich,
    selectedMarke,
    selectedProjekt,
    selectedCompareIds,
    selectedYear,
    selectedTab,
    vm,
    hasData,
    initialize,
    updateSelection,
    setTab,
    toggleCompare,
    clearCompare,
    setCompareByBrand,
  };
}
